import { useMemo, useState } from 'react'
import { Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Snackbar, Alert } from '@mui/material'
import { Add, MoreVert, Edit, Block, CheckCircleOutlined } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listNotificationRules, deactivateNotificationRule, reactivateNotificationRule, type NotificationRule } from '../../api/reguliNotificare'
import { errorMessage } from '../../api/client'
import NotificationRuleFormDialog from './NotificationRuleFormDialog'

export default function NotificationRulesPage() {
  const [formTarget, setFormTarget] = useState<NotificationRule | null | 'new'>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: NotificationRule } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['administrare', 'reguli-notificare'], queryFn: listNotificationRules })

  const closeMenu = () => setMenuAnchor(null)

  const deactivateMutation = useMutation({
    mutationFn: deactivateNotificationRule,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['administrare', 'reguli-notificare'] }),
    onError: (e) => setError(errorMessage(e)),
  })
  const reactivateMutation = useMutation({
    mutationFn: reactivateNotificationRule,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['administrare', 'reguli-notificare'] }),
    onError: (e) => setError(errorMessage(e)),
  })

  const columns = useMemo<GridColDef<NotificationRule>[]>(() => [
    { field: 'eventType', headerName: 'Tip eveniment', width: 260, renderCell: (p) => <code>{p.row.eventType}</code> },
    { field: 'roleName', headerName: 'Rol destinatar', flex: 1, minWidth: 160 },
    {
      field: 'channels', headerName: 'Canale', width: 160,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5}>
          {p.row.notifyInApp && <Chip size="small" label="in-app" variant="outlined" />}
          {p.row.notifyEmail && <Chip size="small" label="email" variant="outlined" />}
        </Stack>
      ),
    },
    {
      field: 'escalation', headerName: 'Escaladare', width: 220,
      valueGetter: (_v, row) => row.escalationThresholdMinutes != null ? `după ${row.escalationThresholdMinutes} min → ${row.escalationRoleName}` : '—',
    },
    {
      field: 'status', headerName: 'Status', width: 100, sortable: false,
      renderCell: (p) => p.row.isActive
        ? <Chip label="Activă" size="small" color="success" variant="outlined" />
        : <Chip label="Inactivă" size="small" variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Configurare</Typography>
        <Typography variant="inherit" color="text.primary">Reguli de notificare</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Typography variant="h2">Reguli de notificare</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Regulă nouă</Button>
      </Box>
      <Typography variant="body2" color="text.secondary">
        Cui i se trimite o notificare, pe ce canale, și la ce prag de timp se
        escaladează — configurabil fără intervenție de dezvoltare (FR-ADM-015,
        FR-OPE-032/033). Fără o regulă activă pentru un tip de eveniment, nimic
        nu se trimite.
      </Typography>

      <Box sx={{ height: 480, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid
          rows={data ?? []} columns={columns} loading={isLoading} density="comfortable"
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[25, 50]}
        />
      </Box>

      <NotificationRuleFormDialog target={formTarget} onClose={() => setFormTarget(null)} />

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          menuAnchor.row.isActive
            ? (
              <MenuItem key="deactivate" onClick={() => { closeMenu(); deactivateMutation.mutate(menuAnchor.row.id) }}>
                <ListItemIcon><Block fontSize="small" /></ListItemIcon><ListItemText>Dezactivare</ListItemText>
              </MenuItem>
            )
            : (
              <MenuItem key="reactivate" onClick={() => { closeMenu(); reactivateMutation.mutate(menuAnchor.row.id) }}>
                <ListItemIcon><CheckCircleOutlined fontSize="small" /></ListItemIcon><ListItemText>Reactivare</ListItemText>
              </MenuItem>
            ),
        ]}
      </Menu>

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}
